import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { fmtMoney, fmtNum } from "@/lib/format";
import ProductThumb from "@/components/ProductThumb";
import { Loading, EmptyState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Search, Loader2, ListOrdered, Ship, PackageCheck, ShoppingCart, History as HistoryIcon } from "lucide-react";

const EVENT_META = {
  order: { color: "#F59E0B", icon: ListOrdered, label: "Amazon Sale" },
  shipment: { color: "#8B5CF6", icon: Ship, label: "FBA Shipment" },
  receipt: { color: "#10B981", icon: PackageCheck, label: "Warehouse Receipt" },
  other_sale: { color: "#06B6D4", icon: ShoppingCart, label: "Other-Channel Sale" },
};

function SummaryPill({ type, count }) {
  const m = EVENT_META[type];
  const Icon = m.icon;
  return (
    <div className="flex items-center gap-2 rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2" data-testid={`ph-summary-${type}`}>
      <span className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: `${m.color}22` }}><Icon className="h-4 w-4" style={{ color: m.color }} /></span>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#666]">{m.label}</p>
        <p className="text-sm font-bold text-white tnum">{fmtNum(count)}</p>
      </div>
    </div>
  );
}

export default function ProductHistoryTab() {
  const { currency } = useCurrency();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 1) { setResults([]); return; }
      try {
        const r = await api.get(`/products/search?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data.results); setOpen(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const openProduct = useCallback(async (sku) => {
    setSelected(sku); setOpen(false); setQ(""); setLoading(true); setDetail(null);
    try {
      const r = await api.get(`/products/${encodeURIComponent(sku)}/history?currency=${currency}`);
      setDetail(r.data);
    } catch { setDetail(null); }
    finally { setLoading(false); }
  }, [currency]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/products/${encodeURIComponent(selected)}/history?currency=${currency}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [currency, selected]);

  return (
    <div className="space-y-6" data-testid="product-history-tab">
      <div className="max-w-xl">
        <p className="text-sm text-[#A3A3A3] mb-3">Search a SKU or product to see its full timeline — Amazon orders, FBA shipments, warehouse receipts and other-channel sales, all on one line.</p>
        <div ref={boxRef} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666] pointer-events-none" />
          <Input
            data-testid="ph-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q && setOpen(true)}
            placeholder="Search any SKU or product…"
            className="h-10 w-full pl-9 bg-[#111] border-[#262626] text-white rounded-full text-sm focus-visible:ring-[#007AFF]"
          />
          {open && results.length > 0 && (
            <div data-testid="ph-search-results" className="absolute z-50 mt-2 w-full max-h-[50vh] overflow-y-auto rounded-xl border border-[#262626] bg-[#0d0d0d] shadow-2xl p-1.5">
              {results.map((r) => (
                <button key={r.sku} data-testid={`ph-result-${r.sku}`} onClick={() => openProduct(r.sku)}
                  className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-white/5 text-left transition-colors">
                  <ProductThumb src={r.image} size={34} title={r.name} />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{r.name || r.sku}</p>
                    <p className="text-[#666] text-xs tnum truncate">{r.sku}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#4da3ff] bg-[#007AFF]/10 px-2 py-0.5 rounded-full shrink-0">{r.type}</span>
                </button>
              ))}
            </div>
          )}
          {open && q.trim() && results.length === 0 && (
            <div className="absolute z-50 mt-2 w-full rounded-xl border border-[#262626] bg-[#0d0d0d] shadow-2xl px-4 py-6 text-center text-sm text-[#666]">No products match “{q}”.</div>
          )}
        </div>
      </div>

      {!selected ? (
        <EmptyState title="Pick a product" message="Search above and select a SKU to view its complete activity timeline." />
      ) : loading || !detail ? (
        <div className="flex items-center justify-center h-48 text-[#666]"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-6" data-testid="ph-detail">
          <div className="flex items-center gap-4 rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] p-4">
            <ProductThumb src={detail.image} size={64} title={detail.name} />
            <div className="min-w-0">
              <h2 className="font-head text-lg font-bold text-white truncate">{detail.name}</h2>
              <p className="text-[#A3A3A3] tnum text-xs">{detail.sku}{detail.asin ? ` · ASIN ${detail.asin}` : ""}{detail.is_warehouse ? " · Warehouse SKU" : ""}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryPill type="order" count={detail.summary.orders} />
            <SummaryPill type="shipment" count={detail.summary.shipments} />
            <SummaryPill type="receipt" count={detail.summary.receipts} />
            <SummaryPill type="other_sale" count={detail.summary.other_sales} />
          </div>

          <div className="rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] overflow-hidden">
            <div className="p-5 border-b border-[#1a1a1a] flex items-center gap-2"><HistoryIcon className="h-4 w-4 text-[#007AFF]" /><h3 className="font-head font-bold text-white">Timeline</h3><span className="text-xs text-[#666]">{fmtNum(detail.events.length)} events</span></div>
            {detail.events.length ? (
              <div className="p-5">
                <ol className="relative border-l border-[#1f1f1f] ml-3" data-testid="ph-timeline">
                  {detail.events.map((e, i) => {
                    const m = EVENT_META[e.type] || EVENT_META.order;
                    const Icon = m.icon;
                    return (
                      <li key={i} data-testid={`ph-event-${i}`} className="mb-5 ml-6">
                        <span className="absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-[#0f0f0f]" style={{ background: `${m.color}26` }}>
                          <Icon className="h-3.5 w-3.5" style={{ color: m.color }} />
                        </span>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-white flex items-center gap-2">
                              {e.title}
                              {e.status ? <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${m.color}22`, color: m.color }}>{e.status}</span> : null}
                            </p>
                            <p className="text-xs text-[#666] tnum truncate">{e.subtitle}{e.sku ? ` · ${e.sku}` : ""}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-[#A3A3A3] tnum">{e.date || "—"}</p>
                            <p className="text-sm font-semibold tnum" style={{ color: m.color }}>
                              {e.qty ? `${e.qty > 0 ? "" : ""}${fmtNum(e.qty)} u` : ""}{e.amount != null ? `  ${fmtMoney(e.amount, currency)}` : ""}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-[#666]">No activity recorded for this product yet.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
